import * as React from 'react';

interface UseDropdownA11yParams<T extends { key: string }> {
  options: T[];
  selectedKey: string | number;
  onSelect?: (value: T) => void;
}

interface UseDropdownA11yResult<T extends { key: string }> {
  isOpen: boolean;
  activeIndex: number;
  listboxId: string;
  dropdownRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLDivElement>;
  selectedOption: T | undefined;
  toggleDropdown: () => void;
  selectOption: (option: T) => void;
  onTriggerKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onOptionKeyDown: (
    event: React.KeyboardEvent<HTMLLIElement>,
    index: number
  ) => void;
  setOptionRef: (index: number, node: HTMLLIElement | null) => void;
}

export function useDropdownA11y<T extends { key: string }>({
  options,
  selectedKey,
  onSelect,
}: UseDropdownA11yParams<T>): UseDropdownA11yResult<T> {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<Array<HTMLLIElement | null>>([]);
  const listboxId = React.useId();

  const selectedOption = options.find((option) => option.key === selectedKey);
  const selectedIndex = options.findIndex(
    (option) => option.key === selectedKey
  );

  const focusOption = React.useCallback((index: number) => {
    optionRefs.current[index]?.focus();
  }, []);

  const closeDropdown = React.useCallback((focusTrigger = true) => {
    setIsOpen(false);
    setActiveIndex(-1);

    if (focusTrigger) {
      triggerRef.current?.focus();
    }
  }, []);

  const selectOption = React.useCallback(
    (option: T) => {
      onSelect?.(option);
      closeDropdown();
    },
    [closeDropdown, onSelect]
  );

  const openDropdown = React.useCallback(
    (index: number) => {
      if (!options.length) {
        return;
      }

      const safeIndex = index >= 0 ? index : 0;
      setIsOpen(true);
      setActiveIndex(safeIndex);
    },
    [options.length]
  );

  const toggleDropdown = React.useCallback(() => {
    if (isOpen) {
      closeDropdown(false);
      return;
    }

    openDropdown(selectedIndex >= 0 ? selectedIndex : 0);
  }, [closeDropdown, isOpen, openDropdown, selectedIndex]);

  React.useEffect(() => {
    const abortController = new AbortController();
    document.addEventListener(
      'mousedown',
      (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          closeDropdown(false);
        }
      },
      { signal: abortController.signal }
    );

    return () => {
      abortController.abort();
    };
  }, [closeDropdown]);

  React.useEffect(() => {
    if (isOpen && activeIndex >= 0) {
      focusOption(activeIndex);
    }
  }, [activeIndex, focusOption, isOpen]);

  const onTriggerKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!options.length) {
        return;
      }

      switch (event.key) {
        case 'Enter':
        case ' ': {
          event.preventDefault();
          if (isOpen && activeIndex >= 0) {
            selectOption(options[activeIndex]);
          } else {
            openDropdown(selectedIndex >= 0 ? selectedIndex : 0);
          }
          break;
        }
        case 'ArrowDown': {
          event.preventDefault();
          if (!isOpen) {
            openDropdown(selectedIndex >= 0 ? selectedIndex : 0);
            break;
          }

          setActiveIndex((prev) => {
            const next = prev < 0 ? 0 : Math.min(prev + 1, options.length - 1);
            return next;
          });
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          if (!isOpen) {
            openDropdown(
              selectedIndex >= 0 ? selectedIndex : options.length - 1
            );
            break;
          }

          setActiveIndex((prev) => {
            const next = prev < 0 ? options.length - 1 : Math.max(prev - 1, 0);
            return next;
          });
          break;
        }
        case 'Escape': {
          if (isOpen) {
            event.preventDefault();
            closeDropdown();
          }
          break;
        }
        default:
          break;
      }
    },
    [
      activeIndex,
      closeDropdown,
      isOpen,
      openDropdown,
      options,
      selectOption,
      selectedIndex,
    ]
  );

  const onOptionKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLLIElement>, index: number) => {
      switch (event.key) {
        case 'Enter':
        case ' ': {
          event.preventDefault();
          selectOption(options[index]);
          break;
        }
        case 'ArrowDown': {
          event.preventDefault();
          const next = Math.min(index + 1, options.length - 1);
          setActiveIndex(next);
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const prev = Math.max(index - 1, 0);
          setActiveIndex(prev);
          break;
        }
        case 'Home': {
          event.preventDefault();
          setActiveIndex(0);
          break;
        }
        case 'End': {
          event.preventDefault();
          setActiveIndex(options.length - 1);
          break;
        }
        case 'Escape': {
          event.preventDefault();
          closeDropdown();
          break;
        }
        case 'Tab': {
          closeDropdown(false);
          break;
        }
        default:
          break;
      }
    },
    [closeDropdown, options, selectOption]
  );

  const setOptionRef = React.useCallback(
    (index: number, node: HTMLLIElement | null) => {
      optionRefs.current[index] = node;
    },
    []
  );

  return {
    isOpen,
    activeIndex,
    listboxId,
    dropdownRef,
    triggerRef,
    selectedOption,
    toggleDropdown,
    selectOption,
    onTriggerKeyDown,
    onOptionKeyDown,
    setOptionRef,
  };
}
